import React, { useState, useEffect } from 'react'

import {
    Modal,
    Paper,
    Slide,
    Theme,
    CircularProgress,
    TextField,
    Button,
} from '@material-ui/core'
import { Search, GetApp } from '@material-ui/icons'

import { Container } from '../../../components'
import { UnitInspectionCompletionData } from '../../../hooks'
import { toMMDDYYYY } from '../../../helpers'
import { UnitInspectionCompletionTable } from './UnitInspectionCompletionTable'
import { typographyStyle } from '../../../theme/typographyStyle'

interface Props {
    open: boolean
    data: UnitInspectionCompletionData[] | null
    theme: Theme
    loading?: boolean
    apartmentName: string
    onClose: () => void
}

export const UnitInspectionCompletionModal = (props: Props) => {
    const { open, onClose, data, theme, loading = false, apartmentName } = props
    const [searchTerm, setSearchTerm] = useState('')

    // Reset states when modal opens
    useEffect(() => {
        if (open) {
            setSearchTerm('')
        }
    }, [open])

    // Function to handle data export
    const handleExport = () => {
        if (!data) return

        const headers = [
            'Inspection Name',
            'Inspection Type',
            'Unit',
            'Completed By',
            'Completed Date',
            'Start Date',
            'End Date',
        ]
        const csvContent = [
            headers.join(','),
            ...data.map((item) =>
                [
                    item.inspection_name,
                    item.inspection_type,
                    item.unit_name,
                    item.completed_by_name,
                    toMMDDYYYY(new Date(item.completed_date)),
                    toMMDDYYYY(new Date(item.start_date)),
                    toMMDDYYYY(new Date(item.end_date)),
                ].join(','),
            ),
        ].join('\n')

        const blob = new Blob([csvContent], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${apartmentName}_inspection_completions.csv`
        a.click()
        window.URL.revokeObjectURL(url)
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Slide direction="up" in={open}>
                <Paper
                    style={{
                        padding: theme.spacing(2),
                        width: '95vw',
                        height: '85vh',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    <Container
                        direction="column"
                        style={{
                            flex: 1,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        {/* Header with title and export button */}
                        <Container
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: theme.spacing(2),
                                flexShrink: 0,
                            }}
                        >
                            <div
                                style={{
                                    ...typographyStyle(theme.typography.h5),
                                }}
                            >
                                {apartmentName} - Unit Inspection Completion
                                Details
                            </div>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<GetApp />}
                                onClick={handleExport}
                                disabled={!data || loading}
                            >
                                Export CSV
                            </Button>
                        </Container>

                        {/* Search bar */}
                        <Container
                            style={{
                                marginBottom: theme.spacing(2),
                                flexShrink: 0,
                            }}
                        >
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Search in any column..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <Search
                                            color="action"
                                            style={{
                                                marginRight: theme.spacing(1),
                                            }}
                                        />
                                    ),
                                }}
                                size="small"
                            />
                        </Container>

                        {/* Main content */}
                        <Container
                            direction="column"
                            style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                            }}
                        >
                            {loading ? (
                                <Container
                                    style={{
                                        flex: 1,
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <CircularProgress size={60} />
                                </Container>
                            ) : (
                                <UnitInspectionCompletionTable
                                    data={data}
                                    theme={theme}
                                    searchTerm={searchTerm}
                                />
                            )}
                        </Container>
                    </Container>
                </Paper>
            </Slide>
        </Modal>
    )
}
